'use client';

import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  type CrossCoinRisk,
  type FuturesPair,
  type FuturesPairsList,
  fetchCrossCollateralRisk,
  fetchDayCloses,
  fetchFuturesPairs,
  fetchLastDayTickers,
  fetchNewOrdersCount,
  fetchOpenPositions,
  keepStreamedTickers,
  type PairTag,
  type PairTicker,
  type PricePoint,
  summarizeFutures,
  withTickers,
} from '@/lib/api/futures/client';
import { dayClosesKey, FUTURES_PAIRS_QK, LAST_DAY_TICKERS_QK } from '@/lib/api/futures/queryKeys';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { useFuturesTickerWebSocket } from '@/lib/hooks/useFuturesTickerWebSocket';
import { pickTopPairs } from '@/lib/utils/pairFormat';

export const CROSS_COLLATERAL_RISK_QK = 'cross-collateral-risk';
const OPEN_POSITIONS_QK = 'futures-open-positions';
const NEW_ORDERS_COUNT_QK = 'futures-new-orders-count';
/** The user's own positions / orders move with their trading — refetch when older than 15s. */
const ACCOUNT_STALE_TIME = 15_000;
const LIVE_POSITIONS_MS = 5_000;

/** Live readers (the header menu's list) pass a poll interval: uPnL moves with the price. */
function useOpenFuturesPositions(refetchInterval?: number) {
  const { isAuthenticated } = useAuthContext();
  return useQuery({
    queryKey: [OPEN_POSITIONS_QK],
    queryFn: fetchOpenPositions,
    enabled: isAuthenticated,
    staleTime: ACCOUNT_STALE_TIME,
    refetchInterval,
  });
}

/** The header Futures menu's positions list, re-read every 5 s while it shows. */
export function useOpenFuturesPositionsList() {
  const { data, isLoading } = useOpenFuturesPositions(LIVE_POSITIONS_MS);
  return { data, isLoading };
}

/** How many futures positions the user holds; the header's My positions badge. */
export function useOpenFuturesPositionsCount() {
  const { data: positions, isLoading } = useOpenFuturesPositions();
  return { count: positions?.length ?? 0, isLoading };
}

/** Crypto Trading figures for the balance popover; fetched while mounted and signed in. */
export function useFuturesSummary() {
  const { isAuthenticated } = useAuthContext();
  const { data: pairs, isLoading: isPairsLoading } = useFuturesPairs();
  const { data: positions, isLoading: isPositionsLoading } = useOpenFuturesPositions();
  const { data: activeOrders, isLoading: isOrdersLoading } = useQuery({
    queryKey: [NEW_ORDERS_COUNT_QK],
    queryFn: fetchNewOrdersCount,
    enabled: isAuthenticated,
    staleTime: ACCOUNT_STALE_TIME,
  });
  const data = useMemo(() => {
    if (!pairs || !positions || activeOrders === undefined) return undefined;
    return summarizeFutures(positions, pairs, activeOrders);
  }, [pairs, positions, activeOrders]);
  return { data, isLoading: isPairsLoading || isPositionsLoading || isOrdersLoading };
}

/**
 * CROSS margin exposure per settle-coin symbol (upper-case). A coin present in
 * the record backs open cross positions — the Withdrawal / Convert dialogs warn
 * before moving it. Pass `enabled` = dialog open so the check only runs when needed.
 */
export function useCrossCollateralRisk(enabled: boolean) {
  const { isAuthenticated } = useAuthContext();
  return useQuery<Record<string, CrossCoinRisk>>({
    queryKey: [CROSS_COLLATERAL_RISK_QK],
    queryFn: fetchCrossCollateralRisk,
    enabled: enabled && isAuthenticated,
    staleTime: ACCOUNT_STALE_TIME,
  });
}

/** While its ticker socket is down, a live price reader re-reads them this often. */
const LIVE_TICKERS_MS = 15_000;
const SPARKLINE_REFRESH_MS = 5 * 60_000;

/** One `/trade/pairs` read — once per page load — shared by the pairs and the menu tags. */
const PAIRS_LIST_QUERY = {
  queryKey: FUTURES_PAIRS_QK,
  queryFn: () => fetchFuturesPairs(),
  staleTime: Number.POSITIVE_INFINITY,
};

const selectPairs = (list: FuturesPairsList): FuturesPair[] => list.pairs;
const selectActivePairs = (list: FuturesPairsList): FuturesPair[] =>
  list.pairs.filter((pair) => pair.active);
const selectMenuTags = (list: FuturesPairsList): PairTag[] | null => list.menuTags;

/** All pairs' static metadata (symbol, tick/lot size, terminal path). */
export function useFuturesPairs() {
  return useQuery({ ...PAIRS_LIST_QUERY, select: selectPairs });
}

/** ACTIVE pairs' metadata without prices, for a reader that never shows them. */
export function useActiveFuturesPairs() {
  return useQuery({ ...PAIRS_LIST_QUERY, select: selectActivePairs });
}

/** The TradFi menu's tag rows, from the same pairs response. */
export function usePairMenuTags() {
  const { data, isLoading } = useQuery({ ...PAIRS_LIST_QUERY, select: selectMenuTags });
  return { data, isLoading };
}

/** A `/candles/lastDay` read; socket tickers that land while it is in flight are newer, so they stay. */
async function readLastDayTickers({ client }: QueryFunctionContext): Promise<PairTicker[]> {
  const startedAt = Date.now();
  const tickers = await fetchLastDayTickers();
  return keepStreamedTickers(tickers, client.getQueryData(LAST_DAY_TICKERS_QK), startedAt);
}

const LAST_DAY_TICKERS_QUERY = { queryKey: LAST_DAY_TICKERS_QK, queryFn: readLastDayTickers };

interface TickerRefresh {
  staleTime: number;
  /** Poll while the tab is visible; React Query pauses it in the background. */
  refetchInterval?: number;
}

function usePairsWithTickers({ staleTime, refetchInterval }: TickerRefresh) {
  const pairs = useFuturesPairs();
  const tickers = useQuery({
    ...LAST_DAY_TICKERS_QUERY,
    staleTime,
    refetchInterval,
  });
  const data = useMemo(
    () => (pairs.data && tickers.data ? withTickers(pairs.data, tickers.data) : undefined),
    [pairs.data, tickers.data],
  );
  return { data, isLoading: pairs.isLoading || tickers.isLoading, error: pairs.error };
}

/** ACTIVE pairs + 24h prices for the header search; prices refetched when older than 30s. */
export function useSearchPairs() {
  return usePairsWithTickers({ staleTime: 30_000 });
}

/**
 * Keeps the header Futures / TradFi menus' prices live: every pair streams while
 * one is open, on a socket held from the first open on. Also warms the prices on load
 * and re-reads them every 15 s while a menu is open and the socket is down.
 */
export function useMarketMenuTickers(isMenuOpen: boolean) {
  const [hasOpened, setHasOpened] = useState(isMenuOpen);
  if (isMenuOpen && !hasOpened) setHasOpened(true);
  const { isStreaming } = useFuturesTickerWebSocket({
    subscribeAll: isMenuOpen,
    stayConnected: hasOpened,
  });
  useQuery({
    ...LAST_DAY_TICKERS_QUERY,
    staleTime: LIVE_TICKERS_MS,
    refetchInterval: isMenuOpen && !isStreaming ? LIVE_TICKERS_MS : undefined,
  });
}

/**
 * The home Hot Futures card's top `count` ACTIVE pairs with 24h prices. Prices
 * stream over the ticker socket; the 15 s re-read runs only while it is down.
 */
export function useHotPairs(count: number) {
  const { data: pairs } = useFuturesPairs();
  // The pick reads only metadata, so the socket subscribes before the prices land.
  const hotPairIds = useMemo(
    () => pickTopPairs(pairs?.filter((pair) => pair.active) ?? [], count).map((pair) => pair.id),
    [pairs, count],
  );
  const { isStreaming } = useFuturesTickerWebSocket({ pairIds: hotPairIds });
  const { data: livePairs, isLoading } = usePairsWithTickers({
    staleTime: LIVE_TICKERS_MS,
    refetchInterval: isStreaming ? undefined : LIVE_TICKERS_MS,
  });
  const data = useMemo(() => pickTopPairs(livePairs ?? [], count), [livePairs, count]);
  return { data, isLoading };
}

/** A pair's last 24 hourly closes, re-read every 5 minutes. */
export function useDayCloses(pairId: number) {
  const { data, isLoading } = useQuery<PricePoint[]>({
    queryKey: dayClosesKey(pairId),
    queryFn: () => fetchDayCloses(pairId),
    staleTime: SPARKLINE_REFRESH_MS,
    refetchInterval: SPARKLINE_REFRESH_MS,
  });
  return { data, isLoading };
}
